import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { toast } from '@/components/ui/toast/Toast';

import { loginUser } from '../api/auth.api';
import { setCredentials, setBrokerStatus } from '@store/slices/authSlice';
import { fetchBrokerList } from '@store/slices/brokerSlice';
import { setLocal, getLocal, removeLocal } from '@utils/storage';

export const useLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [requestError, setRequestError] = useState('');

    const handlePostLoginRedirect = useCallback(({ activeSubscription, activeTradeSubscription }) => {
        const selectedPlan = getLocal('selectedPlan')?.plan;

        if (activeSubscription || activeTradeSubscription) {
            if (selectedPlan) {
                removeLocal('selectedPlan');
                navigate('/pricing');
                return;
            }
            if (activeTradeSubscription && !activeSubscription) {
                navigate('/trading/broker-accounts');
                return;
            }
            const returnPath = getLocal('current-page')?.pathname;
            navigate(returnPath || '/pages/technical-analysis');
        } else {
            navigate('/pricing');
        }
    }, [navigate]);

    const syncBrokerStatus = useCallback(async (userData) => {
        const result = await dispatch(fetchBrokerList());
        const hasBroker = !!result?.payload?.length;
        dispatch(setBrokerStatus(hasBroker));
        setLocal('user', { ...userData, brokerLoggedIn: hasBroker });
    }, [dispatch]);

    const onLoginSuccess = useCallback(async (userData) => {
        const payload = { ...userData, isLoggedIn: true };
        setLocal('user', payload);
        dispatch(setCredentials(payload));

        syncBrokerStatus(payload);

        handlePostLoginRedirect(payload);
    }, [dispatch, syncBrokerStatus, handlePostLoginRedirect]);

    const handleEmailLogin = useCallback(async (values) => {
        setLoading(true);
        setRequestError('');

        const data = await loginUser({ email: values.email, password: values.password });

        if (data?.code === 200) {
            await onLoginSuccess(data.data);
        } else {
            setRequestError(data?.error || 'Please check your connection and try again');
        }

        setLoading(false);
    }, [onLoginSuccess]);

    const handleGoogleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            setGoogleLoading(true);
            setRequestError('');

            const data = await loginUser({ email: '', password: '', gtoken: tokenResponse.access_token });

            if (data?.code === 200) {
                if (data.data.gUserRegisterRequired) {
                    navigate('/authentication/register', { state: data.data });
                } else {
                    await onLoginSuccess(data.data);
                }
            } else {
                const msg = data?.error || 'Google login failed. Please try again.';
                setRequestError(msg);
                toast.error(msg);
            }

            setGoogleLoading(false);
        },
        onError: () => {
            setRequestError('Google login failed. Please try again.');
            toast.error('Google login failed');
            setGoogleLoading(false);
        },
    });

    return { loading, googleLoading, requestError, handleEmailLogin, handleGoogleLogin };
};